// src/pages/Warehouse/Inventory/InventoryTable.jsx
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Col, Row, Table, Typography, Input, Button, Space, Select, message } from "antd"
import { SearchOutlined, SwapOutlined } from "@ant-design/icons"
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore"
import { db } from "../../../../firebase/config"
import { logInventoryAdjustmentEvent, logRollMovedEvent } from "../Helper/warehouseEvents"

const { Text } = Typography
const nf = new Intl.NumberFormat("en-US")

const LOCATIONS = [
  { label: "Arsoma", value: "Arsoma" },
  { label: "Viva", value: "Viva" },
  { label: "Winder", value: "Winder" },
  { label: "Tape", value: "Tape" },
  { label: "Warehouse", value: "Warehouse" },
]

function normalizeRoll(d) {
  return {
    id: `roll:${d.id}`,
    realId: d.id,
    source: "Roll",
    price: d.price,
    total: d.totalPrice,
    productTypeName: d.productTypeName || "Roll",
    productName:
      d?.barcode ||
      d?.productName ||
      d?.material?.material ||
      d?.material?.supplierMaterialName ||
      "Roll",
    location: d.rollLocation || d.machine || d.destinationMachine || (d.inStock ? "Warehouse" : ""),
    materialName: d?.material?.material || d?.material?.supplierMaterialName || "",
    slogan: d?.material?.slogan || "",
    unit: "m",
    quantity: Number(d?.remainingMeters ?? d?.originalMeters) || 0,
    rollWidth: Number(d?.rollWidth) || null,
    comment: d?.comment || "",
    barcode: d?.barcode || "",
    batchSeries: d?.batchSeries || "",
    _raw: d,
  }
}

function normalizeInventory(d) {
  const qty = Number(d?.quantity ?? d?.qty ?? d?.amount) || 0
  return {
    id: `inv:${d.id}`,
    realId: d.id,
    source: "Inventory",
    price: d.price,
    total: d.totalPrice,
    productTypeName: d.productTypeName || d.productTypeId || "",
    productName: d.productName || "",
    location: d.location || "",
    materialName: d.materialName || d.material || "",
    slogan: "",
    unit: d.unit || "",
    quantity: qty,
    rollWidth: null,
    comment: d?.notes || "",
    barcode: d?.barcode || d?.ean || "",
    batchSeries: d?.batchSeries || "",
    _raw: d,
  }
}

export default function InventoryTable({ location, currentUserId }) {
  const [rolls, setRolls] = useState([])
  const [inventory, setInventory] = useState([])
  const [loadingRolls, setLoadingRolls] = useState(false)
  const [loadingInv, setLoadingInv] = useState(false)

  const [searchText, setSearchText] = useState("")
  const [productFilterActive, setProductFilterActive] = useState(false)
  const searchInputRef = useRef(null)

  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [targetLocation, setTargetLocation] = useState(undefined)
  const [transferring, setTransferring] = useState(false)

  useEffect(() => {
    setLoadingRolls(true)
    const unsub = onSnapshot(
      query(collection(db, "rolls"), orderBy("updated", "desc")),
      (snap) => {
        setRolls(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        setLoadingRolls(false)
      },
      () => setLoadingRolls(false)
    )
    return () => unsub()
  }, [])

  useEffect(() => {
    setLoadingInv(true)
    const unsub = onSnapshot(
      query(collection(db, "warehouseInventory"), orderBy("productName", "asc")),
      (snap) => {
        setInventory(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
        setLoadingInv(false)
      },
      () => setLoadingInv(false)
    )
    return () => unsub()
  }, [])

  const mergedRows = useMemo(() => {
    const a = rolls.map(normalizeRoll)
    const b = inventory.map(normalizeInventory)
    return [...a, ...b]
  }, [rolls, inventory])

  const visibleRows = useMemo(() => {
    if (!location) return mergedRows
    return mergedRows.filter((r) => (r.location || "") === location)
  }, [mergedRows, location])

  const uniq = (arr) =>
    Array.from(new Set(arr.filter((x) => x !== undefined && x !== null && x !== "")))

  const productTypeFilters = useMemo(
    () =>
      uniq(visibleRows.map((r) => r.productTypeName || (r.source === "Roll" ? "Roll" : ""))).map(
        (v) => ({ text: v, value: v })
      ),
    [visibleRows]
  )
  const materialFilters = useMemo(
    () => uniq(visibleRows.map((r) => r.materialName)).map((v) => ({ text: v, value: v })),
    [visibleRows]
  )
  const locationFilters = useMemo(
    () => uniq(visibleRows.map((r) => r.location)).map((v) => ({ text: v, value: v })),
    [visibleRows]
  )
  const widthFilters = useMemo(
    () =>
      uniq(
        visibleRows.map((r) =>
          Number.isFinite(Number(r.rollWidth)) && Number(r.rollWidth) > 0
            ? Number(r.rollWidth)
            : null
        )
      )
        .filter((n) => n !== null)
        .sort((a, b) => a - b)
        .map((n) => ({ text: `${n}`, value: n })),
    [visibleRows]
  )

  const productBarcodeFilter = {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInputRef}
          placeholder="Search product / barcode"
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => {
            confirm()
            setSearchText(selectedKeys[0] || "")
            setProductFilterActive(!!(selectedKeys[0] || ""))
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => {
              confirm()
              setSearchText(selectedKeys[0] || "")
              setProductFilterActive(!!(selectedKeys[0] || ""))
            }}>
            Search
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.()
              setSearchText("")
              setProductFilterActive(false)
              confirm({ closeDropdown: true })
            }}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered || productFilterActive ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const q = String(value || "").toLowerCase()
      const hay = `${record.productName || ""} ${record.barcode || ""}`.toLowerCase()
      return hay.includes(q)
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInputRef.current?.select(), 100)
    },
  }

  const handleTransfer = async () => {
    if (!selectedRowKeys.length) {
      message.warning("Please select items to transfer")
      return
    }
    if (!targetLocation) {
      message.warning("Please select a target location")
      return
    }

    setTransferring(true)
    try {
      const selectedItems = visibleRows.filter((r) => selectedRowKeys.includes(r.id))

      for (const item of selectedItems) {
        const oldLocation = item.location || "Warehouse"

        if (item.source === "Roll") {
          // Update roll location
          await updateDoc(doc(db, "rolls", item.realId), {
            rollLocation: targetLocation,
            updated: serverTimestamp(),
          })

          // Log event
          await logRollMovedEvent({
            rollDoc: item._raw,
            rollDocId: item.realId,
            fromLocation: oldLocation,
            toLocation: targetLocation,
            userId: currentUserId,
          })
        } else {
          // Update inventory location
          await updateDoc(doc(db, "warehouseInventory", item.realId), {
            location: targetLocation,
            updatedAt: serverTimestamp(),
          })

          // Log as adjustment with location change in metadata
          await logInventoryAdjustmentEvent({
            inventoryDoc: item._raw,
            inventoryDocId: item.realId,
            oldQuantity: item.quantity,
            newQuantity: item.quantity,
            reason: `Location changed from ${oldLocation} to ${targetLocation}`,
            userId: currentUserId,
          })
        }
      }

      message.success(`Transferred ${selectedItems.length} item(s) to ${targetLocation}`)
      setSelectedRowKeys([])
      setTargetLocation(undefined)
    } catch (error) {
      console.error("Transfer failed:", error)
      message.error("Failed to transfer items")
    } finally {
      setTransferring(false)
    }
  }

  const showLocationCol = !location

  const columns = [
    {
      title: "Product type",
      dataIndex: "productTypeName",
      align: "center",
      width: 180,
      filters: productTypeFilters,
      onFilter: (value, record) => (record?.productTypeName || "") === value,
      render: (v, r) => v || (r.source === "Roll" ? "Roll" : <Text type="secondary">—</Text>),
    },
    ...(showLocationCol
      ? [
          {
            title: "Location",
            dataIndex: "location",
            align: "center",
            width: 140,
            filters: locationFilters,
            onFilter: (value, record) => (record?.location || "") === value,
            render: (v) => v || <Text type="secondary">—</Text>,
          },
        ]
      : []),
    {
      title: "Unit",
      dataIndex: "unit",
      align: "center",
      width: 90,
      render: (v, r) => (r.source === "Roll" ? "m" : v || <Text type="secondary">—</Text>),
    },
    {
      title: "Qty / Meters",
      dataIndex: "quantity",
      align: "right",
      width: 140,
      sorter: (a, b) => (Number(a?.quantity) || 0) - (Number(b?.quantity) || 0),
      render: (v) =>
        Number.isFinite(Number(v)) ? nf.format(Number(v)) : <Text type="secondary">—</Text>,
    },
    {
      title: "Product / Barcode",
      dataIndex: "productName",
      align: "center",
      ellipsis: true,
      width: 280,
      render: (v) => v || <Text type="secondary">—</Text>,
      ...productBarcodeFilter,
    },
    {
      title: "Price",
      dataIndex: "price",
      align: "center",
      ellipsis: true,
      width: 100,
    },
    {
      title: "Material",
      dataIndex: "materialName",
      align: "center",
      width: 220,
      filters: materialFilters,
      onFilter: (value, record) => (record?.materialName || "") === value,
      render: (v, r) =>
        r.source === "Roll" ? (
          v || <Text type="secondary">—</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Width (mm)",
      dataIndex: "rollWidth",
      align: "right",
      width: 120,
      filters: widthFilters,
      onFilter: (value, record) => Number(record?.rollWidth) === Number(value),
      sorter: (a, b) => (Number(a?.rollWidth) || 0) - (Number(b?.rollWidth) || 0),
      render: (v, r) =>
        r.source === "Roll" ? (
          Number.isFinite(Number(v)) ? (
            nf.format(Number(v))
          ) : (
            <Text type="secondary">—</Text>
          )
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  }

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <Space size="middle" style={{ marginBottom: 12 }}>
            <Select
              style={{ width: 200 }}
              placeholder="Select target location"
              value={targetLocation}
              onChange={setTargetLocation}
              options={LOCATIONS}
              allowClear
            />
            <Button
              type="primary"
              icon={<SwapOutlined />}
              onClick={handleTransfer}
              loading={transferring}
              disabled={!selectedRowKeys.length || !targetLocation}>
              Transfer {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
            </Button>
            {selectedRowKeys.length > 0 && (
              <Button onClick={() => setSelectedRowKeys([])}>Clear selection</Button>
            )}
          </Space>
        </Col>

        <Col span={24}>
          <Table
            rowKey={(r) => r.id}
            loading={loadingRolls || loadingInv}
            dataSource={visibleRows}
            columns={columns}
            rowSelection={rowSelection}
            bordered
            size="small"
            pagination={{ pageSize: 200, showSizeChanger: true }}
          />
        </Col>
      </Row>
    </div>
  )
}
